import { useEffect, useId, useState, type ReactNode } from "react"
import { create } from "zustand"
import { Link, useLocation } from "react-router-dom"
import { isGuestThread, requireLogin, useAuthGate } from "@/lib/authGate"
import { Button } from "../ds"
import { MatterPicker } from "../matter/MatterPicker"
import { useAnswerSession } from "./session"

/** The two places an answer offers "keep this in a workspace": one quiet line while it runs, one top-bar action once it has landed. The picker is the shared MatterPicker. */
function useEntry(threadId: string) {
  const { signedIn } = useAuthGate()
  const caseFile = useAnswerSession((s) => s.caseFile)
  const [picking, setPicking] = useState(false)
  const guest = isGuestThread(threadId, signedIn)
  const start = () => {
    if (guest) return requireLogin("login_required", window.location.pathname)
    setPicking(true)
  }
  const picker = picking ? (
    <div className="wse-pop">
      <MatterPicker
        type="nira"
        sessionId={threadId}
        currentCaseId={caseFile?.id ?? null}
        onDone={(m) => { useAnswerSession.getState().setCaseFile(m); setPicking(false) }}
        onClose={() => setPicking(false)}
      />
    </div>
  ) : null
  return { caseFile, start, picker }
}

/** The shared "keep this in a workspace" card: a title, one sentence, and the actions. Used under the answer's progress list and under a simulator draft's run. */
export function WseCard({ title, text, children }: { title: string; text: string; children: ReactNode }) {
  const titleId = useId()
  return (
    <section className="wse-card" aria-labelledby={titleId}>
      <h3 id={titleId} className="wse-card-title">{title}</h3>
      <p className="wse-card-text">{text}</p>
      <div className="wse-card-acts">{children}</div>
    </section>
  )
}

/** While the answer is being written: a short card under the progress list. Nothing when the thread is already in a workspace. */
export function WorkspaceLine({ threadId }: { threadId: string }) {
  const { caseFile, start, picker } = useEntry(threadId)
  const here = useLocation()
  if (!threadId || caseFile) return null
  return (
    <WseCard title="Keep this in a workspace" text="A workspace is a folder for one client matter. It keeps this research, your drafts and the notices and files for that case together, so you can come back to it, add a notice or reply, and carry on drafting later.">
      <span className="wse-pick">
        <Button variant="primary" size="sm" aria-haspopup="dialog" onMouseDown={(e) => e.stopPropagation()} onClick={start}>Add to workspace</Button>
        {picker}
      </span>
      <Link className="wse-link wse-about" to="/workspaces/about" state={{ from: here.pathname + here.search }}>What&rsquo;s a workspace?</Link>
    </WseCard>
  )
}

const FOLDER = <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6.5A1.5 1.5 0 0 1 4.5 5h3.2l1.6 1.8h6.2A1.5 1.5 0 0 1 17 8.3v6.2a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 3 14.5z" /></svg>

/**
 * The top-bar action exists twice (the bar and, on a phone, its ⋯ menu, which closes on every click), so the
 * picker's state lives here and one WorkspacePickerHost, mounted once by the page, draws the picker.
 */
const useSave = create<{ picking: boolean; opened: boolean; set: (p: Partial<{ picking: boolean; opened: boolean }>) => void }>((set) => ({
  picking: false, opened: false, set: (p) => set(p),
}))

/** In the top bar once the answer has landed. Saved here: "In Acme Traders" linking to it. Already in one when the page loaded: nothing (the breadcrumb names it). */
export function SaveToWorkspace({ threadId }: { threadId: string }) {
  const { signedIn } = useAuthGate()
  const caseFile = useAnswerSession((s) => s.caseFile)
  const opened = useSave((s) => s.opened)
  if (!threadId) return null
  if (caseFile) {
    return opened ? (
      <Link className="nr-bar-act wse-in" to={`/workspace/${encodeURIComponent(caseFile.id)}`} aria-label={`In ${caseFile.title}`} title={caseFile.title}>
        {FOLDER}
        <span>In {caseFile.title}</span>
      </Link>
    ) : null
  }
  const start = () => {
    if (isGuestThread(threadId, signedIn)) return requireLogin("login_required", window.location.pathname)
    useSave.getState().set({ picking: true, opened: true })
  }
  return (
    <button type="button" className="nr-bar-act" aria-label="Save to workspace" title="Save to workspace" aria-haspopup="dialog" onMouseDown={(e) => e.stopPropagation()} onClick={start}>
      {FOLDER}
      <span>Save to workspace</span>
    </button>
  )
}

/** Draws the picker the top-bar action opens, under the bar. Mounted once per answer page. */
export function WorkspacePickerHost({ threadId }: { threadId: string }) {
  const picking = useSave((s) => s.picking)
  const caseFile = useAnswerSession((s) => s.caseFile)
  useEffect(() => () => useSave.getState().set({ picking: false, opened: false }), [threadId])
  if (!picking || !threadId) return null
  const close = () => useSave.getState().set({ picking: false })
  return (
    <div className="wse-host">
      <MatterPicker
        type="nira"
        sessionId={threadId}
        currentCaseId={caseFile?.id ?? null}
        onDone={(m) => { useAnswerSession.getState().setCaseFile(m); close() }}
        onClose={close}
      />
    </div>
  )
}
